import {invalidateOnSuccess, omneticApi} from '@dms/api/base';
import {BaseVoidRequestSchema} from '@dms/api/shared';
import {buildBracketSearchParams} from '@dms/api/utils';

import {
  CancelCarisServiceRequestApiArg,
  CancelCarisServiceRequestApiResponse,
  CancelCarisServiceRequestApiResponseSchema,
  CreateCarisServiceRequestApiArg,
  CreateCarisServiceRequestApiResponse,
  CreateCarisServiceRequestApiResponseSchema,
  GetVehicleCarisServiceRequestsApiArg,
  GetVehicleCarisServiceRequestsApiResponse,
  GetVehicleCarisServiceRequestsApiResponseSchema,
} from './types';

export const carisServiceRequestApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getVehicleCarisServiceRequests: build.query<
      GetVehicleCarisServiceRequestsApiResponse,
      GetVehicleCarisServiceRequestsApiArg
    >({
      query: (queryArg) => ({
        // The state filter must reach the wire as a bracket array, which fetchBaseQuery
        // cannot produce from `params` (it comma-joins arrays)
        url: `/dms/v1/caris/vehicle-service-request?${buildBracketSearchParams(queryArg)}`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'CarisServiceRequest', id: `vehicle-${queryArg.vehicleId}`},
      ],
      extraOptions: {
        requestSchema: BaseVoidRequestSchema,
        responseSchema: GetVehicleCarisServiceRequestsApiResponseSchema,
      },
    }),
    createCarisServiceRequest: build.mutation<
      CreateCarisServiceRequestApiResponse,
      CreateCarisServiceRequestApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/caris/service-request',
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: CreateCarisServiceRequestApiArg) => [
        {type: 'CarisServiceRequest', id: `vehicle-${queryArg.body.vehicleId}`},
      ]),
      extraOptions: {
        requestSchema: BaseVoidRequestSchema,
        responseSchema: CreateCarisServiceRequestApiResponseSchema,
      },
    }),
    cancelCarisServiceRequest: build.mutation<
      CancelCarisServiceRequestApiResponse,
      CancelCarisServiceRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/caris/service-request/${queryArg.serviceRequestId}/cancel`,
        method: 'PUT',
      }),
      // Invalidates on error too: a rejected cancel means Caris already accepted the request,
      // so the cached list is stale and still offers the cancel action
      invalidatesTags: (result, error, queryArg) => [
        {type: 'CarisServiceRequest', id: `vehicle-${queryArg.vehicleId}`},
      ],
      extraOptions: {
        requestSchema: BaseVoidRequestSchema,
        responseSchema: CancelCarisServiceRequestApiResponseSchema,
      },
    }),
  }),
});

export const {
  useGetVehicleCarisServiceRequestsQuery,
  useCreateCarisServiceRequestMutation,
  useCancelCarisServiceRequestMutation,
} = carisServiceRequestApi;
